import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ShiftTemplateModal = ({ onClose, onApplyTemplate }) => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');

  const templates = [
    {
      id: 1,
      name: 'Standard 3-Shift Rotation',
      description: 'Morning, afternoon, and night shifts with 8-hour coverage',
      shifts: 3,
      workers: 24,
      coverage: '24/7'
    },
    {
      id: 2,
      name: '2-Shift Day Coverage',
      description: 'Morning and afternoon shifts for day operations',
      shifts: 2,
      workers: 16,
      coverage: '16 hours'
    },
    {
      id: 3,
      name: 'Weekend Skeleton Crew',
      description: 'Minimal staffing for weekend maintenance',
      shifts: 2,
      workers: 8,
      coverage: '12 hours'
    },
    {
      id: 4,
      name: 'Peak Production Schedule',
      description: 'High-capacity scheduling for peak demand periods',
      shifts: 4,
      workers: 32,
      coverage: '24/7'
    }
  ];

  const handleApply = () => {
    if (selectedTemplate) {
      onApplyTemplate(selectedTemplate);
    }
  };

  const handleCreateTemplate = () => {
    if (newTemplateName?.trim()) {
      console.log('Create template:', newTemplateName);
      setShowCreateForm(false);
      setNewTemplateName('');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-card rounded-xl border border-border w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col m-4">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-foreground">Shift Templates</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Apply pre-configured shift patterns or create new templates
              </p>
            </div>
            <button
              className="flex items-center justify-center w-10 h-10 rounded-xl hover:bg-muted transition-colors duration-250"
              onClick={onClose}
              aria-label="Close"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {showCreateForm ? (
            <div className="bg-muted rounded-lg p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">Create New Template</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Template Name
                  </label>
                  <input
                    type="text"
                    placeholder="Enter template name..."
                    className="w-full px-4 py-2 bg-background rounded-lg text-sm text-foreground placeholder:text-muted-foreground border border-border outline-none focus:ring-2 focus:ring-primary"
                    value={newTemplateName}
                    onChange={(e) => setNewTemplateName(e?.target?.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Description
                  </label>
                  <textarea
                    placeholder="Describe the shift pattern..."
                    className="w-full px-4 py-2 bg-background rounded-lg text-sm text-foreground placeholder:text-muted-foreground border border-border outline-none focus:ring-2 focus:ring-primary resize-none"
                    rows={3}
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleCreateTemplate}
                    disabled={!newTemplateName?.trim()}
                  >
                    Save Template
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowCreateForm(false);
                      setNewTemplateName('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {templates?.map((template) => (
                <div
                  key={template?.id}
                  className={`p-4 rounded-lg border-2 transition-all duration-250 cursor-pointer ${
                    selectedTemplate?.id === template?.id
                      ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 bg-muted'
                  }`}
                  onClick={() => setSelectedTemplate(template)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="text-base font-semibold text-foreground mb-1">
                        {template?.name}
                      </h4>
                      <p className="text-sm text-muted-foreground mb-3">
                        {template?.description}
                      </p>
                      <div className="flex flex-wrap gap-4">
                        <div className="flex items-center gap-2">
                          <Icon name="Clock" size={14} color="var(--color-muted-foreground)" />
                          <span className="text-xs text-muted-foreground">
                            {template?.shifts} Shifts
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Icon name="Users" size={14} color="var(--color-muted-foreground)" />
                          <span className="text-xs text-muted-foreground">
                            {template?.workers} Workers
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Icon name="Calendar" size={14} color="var(--color-muted-foreground)" />
                          <span className="text-xs text-muted-foreground">
                            {template?.coverage} Coverage
                          </span>
                        </div>
                      </div>
                    </div>
                    {selectedTemplate?.id === template?.id && (
                      <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground">
                        <Icon name="Check" size={14} />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-border">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              iconName="Plus"
              onClick={() => setShowCreateForm(!showCreateForm)}
            >
              {showCreateForm ? 'View Templates' : 'Create New'}
            </Button>
            <div className="flex gap-3">
              <Button variant="outline" size="sm" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleApply}
                disabled={!selectedTemplate}
              >
                Apply Template
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShiftTemplateModal;